import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Eraser, Check, ShieldCheck, PenTool, User, UserCheck, Lock } from 'lucide-react';
import { generateDocumentHash } from '../utils/cryptoUtils';
import { DigitalSignature, DocumentType } from '../types';

interface TouchSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentType: DocumentType;
  documentTitle: string;
  providerName: string;
  clientName: string;
  clientDocument?: string;
  totalValue?: number;
  onSaveSignature: (signature: DigitalSignature) => Promise<void> | void;
}

export default function TouchSignatureModal({
  isOpen,
  onClose,
  documentId,
  documentType,
  documentTitle,
  providerName,
  clientName,
  clientDocument,
  totalValue,
  onSaveSignature
}: TouchSignatureModalProps) {
  const [activeSigner, setActiveSigner] = useState<'client' | 'provider'>('client');
  const [clientSignatureData, setClientSignatureData] = useState<string | null>(null);
  const [providerSignatureData, setProviderSignatureData] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isClientSigned, setIsClientSigned] = useState(false);
  const [isProviderSigned, setIsProviderSigned] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#059669'; // Emerald Green
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const currentData = activeSigner === 'client' ? clientSignatureData : providerSignatureData;
    if (currentData) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = currentData;
    }
  }, [activeSigner, clientSignatureData, providerSignatureData]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(setupCanvas, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, activeSigner, setupCanvas]);

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    let clientX = 0;
    let clientY = 0;

    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;

    isDrawingRef.current = true;
    lastPointRef.current = coords;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, 1.25, 0, Math.PI * 2);
      ctx.fillStyle = '#059669';
      ctx.fill();
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawingRef.current || !lastPointRef.current) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    lastPointRef.current = coords;

    if (activeSigner === 'client') {
      setIsClientSigned(true);
    } else {
      setIsProviderSigned(true);
    }
  };

  const stopDrawing = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPointRef.current = null;

    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      if (activeSigner === 'client') {
        setClientSignatureData(dataUrl);
      } else {
        setProviderSignatureData(dataUrl);
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (activeSigner === 'client') {
      setClientSignatureData(null);
      setIsClientSigned(false);
    } else {
      setProviderSignatureData(null);
      setIsProviderSigned(false);
    }
  };

  const handleFinalize = async () => {
    if (!clientSignatureData && !isClientSigned) {
      alert('A assinatura do cliente é obrigatória para validação do documento.');
      setActiveSigner('client');
      return;
    }

    setIsSaving(true);
    try {
      const timestampIso = new Date().toISOString();
      const clientSig = clientSignatureData || canvasRef.current?.toDataURL('image/png') || '';

      const verificationHash = await generateDocumentHash({
        documentId,
        documentType,
        providerName,
        clientName,
        clientDocument,
        timestampIso,
        signatureBase64: clientSig,
        totalValue
      });

      const signatureId = typeof crypto !== 'undefined' && crypto.randomUUID 
        ? crypto.randomUUID() 
        : `sig_${Date.now()}`;

      let geoData: { latitude?: number; longitude?: number; accuracy?: number } | undefined = undefined;
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        try {
          const pos = await new Promise<GeolocationPosition | null>((res) => {
            navigator.geolocation.getCurrentPosition(res, () => res(null), { timeout: 3000 });
          });
          if (pos) {
            geoData = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            };
          }
        } catch {
          // Ignora caso negado ou timeout
        }
      }

      const signatureObject: DigitalSignature = {
        id: signatureId,
        userId: 'current_user',
        documentType,
        documentId,
        providerName,
        providerSignatureBase64: providerSignatureData || undefined,
        clientName,
        clientDocument,
        clientSignatureBase64: clientSig,
        verificationHash,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
        geolocation: geoData,
        signedAt: timestampIso
      };

      await onSaveSignature(signatureObject);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar assinatura:', error);
      alert('Ocorreu um erro ao processar a assinatura digital.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-neutral-950/85 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-xl bg-white border border-zinc-100 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
          >
            {/* Header */}
            <div className="p-4 sm:p-5 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
                  <PenTool className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-extrabold text-zinc-900">
                    Assinatura na Tela
                  </h3>
                  <p className="text-xs text-zinc-500 truncate max-w-xs sm:max-w-sm">
                    {documentTitle}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-full transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Seletor de Assinante */}
            <div className="p-3 bg-zinc-50 border-b border-zinc-100 flex gap-2">
              <button
                type="button"
                onClick={() => setActiveSigner('client')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer active:scale-95 ${
                  activeSigner === 'client'
                    ? 'bg-brand text-white shadow-xs font-black'
                    : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Cliente: {clientName.split(' ')[0]}</span>
                {isClientSigned && <Check className="h-3.5 w-3.5 text-white stroke-[3]" />}
              </button>

              <button
                type="button"
                onClick={() => setActiveSigner('provider')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer active:scale-95 ${
                  activeSigner === 'provider'
                    ? 'bg-brand text-white shadow-xs font-black'
                    : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                <UserCheck className="h-4 w-4" />
                <span>Prestador: {providerName.split(' ')[0]}</span>
                {isProviderSigned && <Check className="h-3.5 w-3.5 text-white stroke-[3]" />}
              </button>
            </div>

            {/* Canvas */}
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-600 font-medium">
                  {activeSigner === 'client' 
                    ? `Assinatura de: ${clientName} ${clientDocument ? `(${clientDocument})` : ''}` 
                    : `Assinatura de: ${providerName}`}
                </span>
                <button
                  type="button"
                  onClick={clearCanvas}
                  className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-600 py-1 px-2.5 rounded-lg hover:bg-zinc-100 cursor-pointer active:scale-95"
                >
                  <Eraser className="h-3.5 w-3.5" />
                  <span>Limpar</span>
                </button>
              </div>

              {/* Box do Canvas Touch */}
              <div className="relative w-full h-52 bg-zinc-50 border-2 border-dashed border-zinc-200 rounded-2xl overflow-hidden touch-none flex items-center justify-center">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full h-full cursor-crosshair"
                />

                <div className="absolute bottom-5 left-6 right-6 border-b border-zinc-200 pointer-events-none flex justify-between items-center text-[10px] text-zinc-400 uppercase tracking-widest px-1">
                  <span>Rubrica / Assinatura</span>
                  <span>{activeSigner === 'client' ? 'Cliente' : 'Prestador'}</span>
                </div>
              </div>

              {/* Auditoria Jurídica */}
              <div className="mt-3.5 p-3.5 bg-emerald-50/60 rounded-2xl border border-emerald-100 flex items-start gap-2.5 text-[11px] text-zinc-600">
                <ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                <div className="space-y-0.5 leading-relaxed">
                  <p className="text-zinc-900 font-bold">Assinatura Eletrônica (Lei 14.063/20 & Art. 107 CC)</p>
                  <p className="text-zinc-500">
                    Gera carimbo com hash SHA-256 de 256 bits, data/hora ISO, dispositivo e geolocalização para validade jurídica e integridade do documento.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200 transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleFinalize}
                disabled={isSaving || (!isClientSigned && !clientSignatureData)}
                className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-sm shadow-brand/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-95"
              >
                {isSaving ? (
                  <span>Processando...</span>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    <span>Confirmar Documento</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
